import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import IntranetLayout from "@/components/IntranetLayout";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Plus, ClipboardCheck, FileText, Wrench, ShieldAlert } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

type Project = {
  id: string;
  project_number: string | null;
  type: "EAR" | "MAR" | "MOC";
  title: string;
  stage: string;
  vessel_name: string | null;
  estimated_cost_usd: number;
  created_by: string;
  created_at: string;
};

const stageColor: Record<string, string> = {
  startup: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  in_progress: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  awaiting_approval: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  completed: "bg-teal-500/15 text-teal-300 border-teal-500/30",
  rejected: "bg-red-500/15 text-red-300 border-red-500/30",
};

const typeIcon: Record<string, any> = { EAR: Wrench, MAR: ShieldAlert, MOC: FileText };

const stageLabel = (s: string) => s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

export default function EarMarList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [pendingApprovalIds, setPendingApprovalIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"all" | "mine" | "approvals">("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const [{ data: ps }, { data: apps }] = await Promise.all([
        supabase.from("ear_mar_projects").select("*").order("created_at", { ascending: false }),
        supabase.from("ear_mar_approvals").select("project_id, approver_user_id, decision").eq("decision", "pending"),
      ]);
      if (!active) return;
      setProjects((ps || []) as any);
      const mine = new Set<string>((apps || []).filter((a: any) => a.approver_user_id === user?.id).map((a: any) => a.project_id));
      setPendingApprovalIds(mine);
      setLoading(false);
    })();
    return () => { active = false; };
  }, [user?.id]);

  const filtered = projects.filter(p => {
    if (tab === "mine" && p.created_by !== user?.id) return false;
    if (tab === "approvals" && !pendingApprovalIds.has(p.id)) return false;
    if (q && !(`${p.project_number} ${p.title} ${p.vessel_name ?? ""}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  return (
    <IntranetLayout>
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold tracking-[3px] uppercase text-teal/70 mb-1">Engineering & Marine</p>
          <h1 className="text-3xl font-bold flex items-center gap-2"><ClipboardCheck className="w-7 h-7 text-teal" /> EAR / MAR Hub</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Vessel ↔ shoreside collaboration for Engineering & Marine Assistance Requests and MOCs. Documents, tasks,
            chat, deliverables and approvals — all gated to assignees and approvers.
          </p>
        </div>
        <Button onClick={() => navigate("/employee/ear-mar/new")} className="bg-teal hover:bg-teal/90"><Plus className="w-4 h-4 mr-1" /> New Project</Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
          <TabsList>
            <TabsTrigger value="all">All Visible</TabsTrigger>
            <TabsTrigger value="mine">Created by Me</TabsTrigger>
            <TabsTrigger value="approvals">Awaiting My Approval {pendingApprovalIds.size > 0 && <Badge className="ml-2 bg-amber-500/20 text-amber-300">{pendingApprovalIds.size}</Badge>}</TabsTrigger>
          </TabsList>
        </Tabs>
        <Input placeholder="Search number, title, vessel…" value={q} onChange={e => setQ(e.target.value)} className="max-w-xs" />
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
          No projects yet. Click <strong>New Project</strong> to create the first EAR, MAR or MOC.
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map(p => {
            const Icon = typeIcon[p.type] || FileText;
            return (
              <Link key={p.id} to={`/employee/ear-mar/${p.id}`}>
                <Card className="hover:border-teal/50 transition">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="rounded-md bg-teal/10 text-teal p-2"><Icon className="w-5 h-5" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-bold tracking-wider text-muted-foreground">{p.project_number}</span>
                        <Badge variant="outline">{p.type}</Badge>
                        <Badge className={stageColor[p.stage]}>{stageLabel(p.stage)}</Badge>
                        {p.estimated_cost_usd > 50000 && <Badge className="bg-purple-500/15 text-purple-300 border-purple-500/30">COO route</Badge>}
                      </div>
                      <p className="font-semibold mt-0.5 truncate">{p.title}</p>
                      <p className="text-xs text-muted-foreground">{p.vessel_name || "No vessel"} · Est. ${p.estimated_cost_usd.toLocaleString("en-US")}</p>
                    </div>
                    <div className="text-[11px] text-muted-foreground">{new Date(p.created_at).toLocaleDateString("en-US")}</div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </IntranetLayout>
  );
}
